// Seizure, on a real patient: at each contact, the envelope of the patient's own recording beside
// the model fitted to them, lined up at the onset. Each keeps its own clock: the recording's below,
// in seconds from the clinically marked onset; the model's above, from its start, where the scene's
// clock runs. One electrode at a time (a grid in blocks of eight contacts); the picker names each by
// the region most of its contacts sit in. Each envelope is drawn on its own scale (they're
// different measures: the recording's line length as a robust z, the model's log power rise), the
// same scale for every contact, so a contact that stays quiet reads as quiet.

import { useMemo } from 'react';
import { useAtlas } from '../app/store.ts';
import { mainSlot, timelineOf } from '../app/scene.ts';
import { openedSlot } from '../app/sims.ts';
import { Chart, type ChartModel, type Lane } from '../ui/Chart.tsx';
import { formatTick } from '../ui/chart.ts';
import { SERIES } from '../ui/scales.ts';
import type { Catalogue } from '../data/catalogue.ts';
import type { DynamicsSim } from '../data/sims/dynamics.ts';
import { frameTime } from '../data/sims/time.ts';
import { contactSeries, liveliestGroup, modalityOf, openComparison, type ContactGroup, type PatientComparison } from '../data/sims/comparison.ts';
import { Frame, OverlayKey, SwatchKey, useLoaded } from './Traces.tsx';

/** The recording in design's trace colour (scale.trace.line); the model warm, like the activity it paints in 3D. */
export const PATIENT_COLOUR = '#dce4ee';
export const MODEL_COLOUR = SERIES[1]!;
const MODEL_FILL = 'rgba(255, 154, 77, 0.16)';
const HEIGHT = 240;
/** A lane in display units: 0 is each envelope's baseline, 1 its scale. */
const LANE_Y: [number, number] = [-0.3, 1.12];
/** Readout units for the envelopes' `units`. */
export const UNIT: Record<string, string> = { robust_z_log_line_length: 'z', log_power_rise_model: '' };

const seconds = (x: number) => `${formatTick(x).replace('-', '−')} s`;

/** A parcel's name in the loaded pack, through the run's own parcel-to-structure map. */
export function regionName(sim: DynamicsSim, catalogue: Catalogue | null, parcel: string): string {
  if (!parcel) return '';
  const id = sim.parcels.find((p) => p.source_name === parcel)?.structure;
  return (id && catalogue?.byId.get(id)?.name) || parcel.replace(/_/g, ' ');
}

export function stripModel(c: PatientComparison, group: ContactGroup): ChartModel {
  const lanes: Lane[] = group.contacts.map((k) => {
    const label = c.contacts[k]!.label;
    const model = contactSeries(c.model, k, c.onset, c.scale.model);
    const patient = contactSeries(c.observed, k, 0, c.scale.observed);
    return {
      label,
      y: LANE_Y,
      ticks: 'none',
      series: [
        { label: `${label} model`, colour: MODEL_FILL, x: model.x, y: model.y, style: 'area', readout: false },
        { label: `${label} · patient`, colour: PATIENT_COLOUR, x: patient.x, y: patient.y, width: 1, readoutY: patient.raw, unit: UNIT[c.observed.units] ?? '' },
        { label: `${label} · model`, colour: MODEL_COLOUR, x: model.x, y: model.y, width: 1.5, readoutY: model.raw, unit: UNIT[c.model.units] ?? '' },
      ],
    };
  });
  const last = (a: PatientComparison['model']['axis']) => frameTime(a, a.frames - 1);
  const lo = Math.min(c.observed.axis.t0, c.model.axis.t0 - c.onset);
  const hi = Math.max(last(c.observed.axis), last(c.model.axis) - c.onset);
  return {
    x: { domain: [lo, hi], format: seconds, name: { text: 'Patient', colour: PATIENT_COLOUR }, title: 'from clinical onset' },
    x2: { shift: c.onset, format: seconds, extent: [c.model.axis.t0, last(c.model.axis)], name: { text: 'Model', colour: MODEL_COLOUR }, title: 'from model start' },
    lanes,
    events: [{ x: 0, label: 'Onset' }],
    gutter: 72,
    readout: 'lane',
  };
}

export function PatientTraces() {
  const o = openedSlot('main');
  const sim = o?.format === 'dynamics' ? (o.sim as DynamicsSim) : null;
  const wanted = useAtlas((s) => s.traceView.group);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const setTime = useAtlas((s) => s.setTime);
  const end = useAtlas((s) => timelineOf(s)?.duration ?? 0);
  const catalogue = useAtlas((s) => s.catalogue);
  // SEEG or ECoG, from the index's name for the run while the header is the one it was written for.
  const modality = useAtlas((s) => modalityOf(mainSlot(s)?.annotation?.label));
  const loaded = useLoaded(() => (sim ? openComparison(sim) : null), [sim]);
  const c = loaded.value;
  const group = c ? (c.groups.find((g) => g.id === wanted) ?? liveliestGroup(c)) : undefined;
  const model = useMemo(() => (c && group ? stripModel(c, group) : null), [c, group]);
  const onset = c?.onset ?? 0;
  const cursor = useMemo(() => (t: number) => t - onset, [onset]);
  if (!sim) return null;
  const controls =
    c && c.groups.length > 1 ? (
      <select className="select traces-select" aria-label="Contacts" value={group?.id ?? ''} onChange={(e) => setTraceView({ group: e.target.value })}>
        {c.groups.map((g) => {
          const name = regionName(sim, catalogue, g.parcel).replace(/^Left /, 'L ').replace(/^Right /, 'R ');
          return <option key={g.id} value={g.id}>{name ? `${g.label} · ${name}` : g.label}</option>;
        })}
      </select>
    ) : null;
  const keyBar = (
    <>
      <SwatchKey
        items={[
          { label: 'Patient', colour: PATIENT_COLOUR },
          { label: 'Model', colour: MODEL_COLOUR },
        ]}
      />
      <OverlayKey slot="main" />
    </>
  );
  const who = c?.patient ?? 'Patient';
  return (
    <Frame title={modality ? `${who} · ${modality}` : who} sub={group ? `${group.contacts.length} contacts` : undefined} controls={controls} keyBar={keyBar}>
      {model ? (
        <Chart
          label={`${who}'s recorded seizure and the model fitted to it, at ${group?.label ?? 'each'} contacts, aligned at the onset`}
          model={model}
          height={HEIGHT}
          cursor={cursor}
          onSeek={(x) => setTime(Math.min(end, Math.max(0, x + onset)))}
        />
      ) : (
        <div className="traces-empty">{loaded.error ?? 'Loading…'}</div>
      )}
    </Frame>
  );
}
